import { ChangeEvent, JSX, useEffect, useState } from "react";
import {
  Dialog_VM,
  DialogFrame,
  useDialog,
} from "../../../components/Dialogs/dialog-base";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import LoadingElement from "../../../components/loading-element/loading-element";
import { Organization } from "../../../../core/models/entities/Organization";
import { useOrganizationRepository } from "../../../../data/repositories/OrganizationRepository";
import { PageQuery } from "../../../../utils/models/pageQuery";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../../shared/hooks/useComponentStateMachine";
import { CustomInput } from "../../../components/Input/CustomInput";
import { InputWrapper } from "../../../components/Input/InputWrapper";
import { InputLabel } from "../../../components/Input/InputLabel";
import { CustomAutoComplete } from "../../../components/Input/CustomAutoComplete";
import { VendorSearchAutoComplete } from "../../../components/Input/VendorSearchAutoComplete";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowDown } from "@fortawesome/free-solid-svg-icons";
import { extensionsToMimeTypesString } from "../../logo-editor/componenets/ExtensionsToMimeTypesString";
import { useLogoActionHandler } from "../../logo-list/hooks/useLogoActionsHandler";
import { useNavigate } from "react-router";
import { Role } from "../../../../core/models/entities/Role";
import { Profile } from "../../../../core/models/entities/Profile";
import { useErrorToast } from "../../../components/Toast/Toast";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { SimpleLogoPanel } from "../../design-editor/design-editor-tool/DesignEditorTool.component";
import { useLogoRepository } from "../../../../data/repositories/LogoRepository";
import {
  Color,
  Font,
  LogoOptions,
  TransferMethod,
} from "../../../../core/models/entities/LogoOptions";
import { useLogoContext } from "../../design-editor2/contexts/logo-options.context";
import { useTranslation } from "react-i18next";
import { enviroment } from "../../../../enviroment";
import {
  UploadPreview,
  UploadPreviewState,
} from "../../logo-editor/componenets/UploadPreview";
import {
  clearUploadRetryRequest,
  peekUploadRetryRequest,
  previewUrlForFile,
  UploadRetryRequest,
} from "../../logo-editor/functions/uploadPending";
import { isUploadKindPending } from "../../../../utils/functions/productPending";

// Flag REACT_APP_UPLOAD_PENDING_ENABLED - build-time constant.
const uploadPendingEnabled = enviroment.uploadPendingEnabled;

interface Props {
  component: JSX.Element;
  open: () => void;
}

export function useCreateLogoDialog({
  onContinue,
  onCancel,
  options,
}: {
  onContinue: () => Promise<void>;
  onCancel?: () => void;
  options: LogoOptions;
}): Props {
  const { profile, organizationId } = useProfileContext();
  const { useAllCustomers } = useOrganizationRepository();
  const {
    data: customers,
    isError,
    isLoading,
    isSuccess,
  } = useAllCustomers({ page: { index: 0, size: 0 } } as PageQuery);

  const navigate = useNavigate();

  const { state, dataTransition } = useComponentStateMachine();
  dataTransition(isLoading, isSuccess, isError);

  const [loading, setLoading] = useState<boolean>(false);
  // Flag REACT_APP_UPLOAD_PENDING_ENABLED: the file being uploaded, painted
  // in the dialog from t~0 (object URL for raster, a file card for
  // pdf/ai/eps) instead of the full spinner. Always null with the flag off.
  const [uploadPreview, setUploadPreview] = useState<UploadPreviewState | null>(
    null
  );

  const dialog = useDialog();

  const {
    handleCreateLogo,
    handleCreatePlaceholderNameLogo,
    handleCreatePlaceholderNumberLogo,
    handleCreateTextLogo,
  } = useLogoActionHandler();

  function onSubmit() {
    setLoading(true);
    onContinue().finally(() => {
      setLoading(false);
      dialog.closeDialog();
    });
  }

  function handleCancel() {
    if (onCancel) onCancel();
    dialog.closeDialog();
  }

  function handleUpload(file: File, title: string, orgId?: number) {
    const id = orgId ?? organizationId;
    // We should never get to this point in the system if there isn't an available orgId.
    if (!id) return;
    setLoading(true);
    if (uploadPendingEnabled) {
      // "Upload stands alone" (design 14/9 §5.2): show the user's own file
      // at once while the request runs. The object URL is handed to the
      // action handler: a worker-owned answer (app117 down/slow) keeps it as
      // the editor's canvas bitmap (pendingUploadFiles owns it from then
      // on); a finished answer or an error revokes it here as today's
      // preview does. The navigation is exactly today's - the editor mounts
      // on the seeded detail entry.
      const previewUrl = previewUrlForFile(file);
      setUploadPreview({ file, previewUrl });
      let handedOver = false;
      handleCreateLogo(file, id, title, previewUrl)
        .then((newLogo) => {
          handedOver = isUploadKindPending(newLogo);
          onSubmit();
          navigate(`/logo/create?logo=${newLogo.id}&organization=${id}`);
          dialog.closeDialog();
        })
        // A failed request (the synchronous deterministic rejection while
        // app117 is up - "Logo size is too big", "Too many artboards" - or a
        // network error) was already toasted by the action handler; the
        // form below is still mounted (hidden, not unmounted, while the
        // preview shows) with the step, customer, title and file the user
        // chose, so they simply pick another file. Nothing to rethrow.
        .catch(() => undefined)
        .finally(() => {
          setLoading(false);
          setUploadPreview(null);
          if (!handedOver && previewUrl) URL.revokeObjectURL(previewUrl);
        });
      return;
    }
    handleCreateLogo(file, id, title)
      .then((newLogo) => {
        onSubmit();
        navigate(`/logo/create?logo=${newLogo.id}&organization=${id}`);
        dialog.closeDialog();
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function handleCreate(
    type: string,
    text?: string,
    color?: Color,
    font?: Font,
    size?: number,
    transferMethod?: TransferMethod,
    customerId?: number,
  ) {
    const isAdmin = profile?.userOrganizationInformations?.role === Role.Admin;
    // For Admin users, the selected vendor ID is passed as customerId from the dialog
    const orgId = isAdmin ? (customerId ?? 0) : (organizationId ?? 0);
    const effectiveCustomerId = isAdmin ? undefined : customerId;
    const getCreateFunction = () => {
      switch (type) {
        case "text":
          return () => {
            const methodKey =
              transferMethod?.type?.toLowerCase() === "transfer"
                ? transferMethod?.typeAndKey
                : undefined;
            return handleCreateTextLogo(
              text!,
              color!.id,
              size!,
              font!.key,
              orgId,
              effectiveCustomerId,
              methodKey,
            );
          };
        case "number":
          return () =>
            handleCreatePlaceholderNumberLogo(
              color!.id,
              size!,
              font!.key,
              transferMethod!.typeAndKey,
              orgId,
              effectiveCustomerId,
            );
        case "name":
          return () =>
            handleCreatePlaceholderNameLogo(
              color!.id,
              size!,
              font!.key,
              orgId,
              effectiveCustomerId,
              transferMethod?.typeAndKey,
            );
      }
    };

    const create = getCreateFunction();
    if (create) {
      setLoading(true);
      create()
        .then((logo) => {
          onSubmit();
          dialog.closeDialog();
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  return {
    component:
      state === ComponentState.Loading ? (
        <DialogFrame dialogOption={dialog}>
          <div className="h-80">
            <LoadingElement transparent />
          </div>
        </DialogFrame>
      ) : (
        <DialogFrame dialogOption={dialog}>
          {/* Flag REACT_APP_UPLOAD_PENDING_ENABLED: while a file is being
              uploaded the dialog shows the file itself (UploadPreview) in
              place of the form + spinner. The form stays MOUNTED behind it
              (hidden, skeptic round 1): a failed request brings it back with
              the step, customer, title and file intact - exactly what
              today's spinner-over-the-form preserves. uploadPreview is
              always null with the flag off, and the flag-off branch renders
              the form without any wrapper = today's DOM. */}
          {loading && !uploadPreview && <LoadingElement transparent />}
          {uploadPendingEnabled ? (
            <>
              {uploadPreview && (
                <UploadPreview
                  file={uploadPreview.file}
                  previewUrl={uploadPreview.previewUrl}
                />
              )}
              <div hidden={!!uploadPreview}>
                <CreateLogoDialogComponent
                  dialog={dialog}
                  options={options}
                  onCreate={handleCreate}
                  onUpload={handleUpload}
                  onCancel={handleCancel}
                  onSubmit={onSubmit}
                  customers={customers?.results as Organization[]}
                  profile={profile!}
                  role={profile!.userOrganizationInformations.role}
                />
              </div>
            </>
          ) : (
            <CreateLogoDialogComponent
              dialog={dialog}
              options={options}
              onCreate={handleCreate}
              onUpload={handleUpload}
              onCancel={handleCancel}
              onSubmit={onSubmit}
              customers={customers?.results as Organization[]}
              profile={profile!}
              role={profile!.userOrganizationInformations.role}
            />
          )}
        </DialogFrame>
      ),
    open: dialog.openDialog,
  };
}

interface CreateLogoDialogComponentProps {
  dialog: Dialog_VM;
  onCancel?: () => void;
  onSubmit?: () => void;
  onCreate?: (
    type: string,
    text?: string,
    color?: Color,
    font?: Font,
    size?: number,
    transferMethod?: TransferMethod,
    customerId?: number,
  ) => void;
  onUpload: (file: File, title: string, orgId?: number) => void;
  profile: Profile;
  customers: Organization[];
  options: LogoOptions;
  role: Role;
}

export function CreateLogoDialogComponent({
  onCreate,
  onUpload,
  customers,
  profile,
  options,
  role,
}: CreateLogoDialogComponentProps) {
  const { t } = useTranslation();
  const userRole = profile?.userOrganizationInformations?.role;
  const orgId = profile?.userOrganizationInformations?.organizationId;
  const { generateNumberLogoPreview, generateTextLogoPreview } =
    useLogoRepository();
  // "Upload another file" (flag REACT_APP_UPLOAD_PENDING_ENABLED, §5.6): a
  // waiting retry request - the dialog is mounted per open (MUI Modal
  // unmounts its children when closed) and only once the customers query
  // has succeeded (the hook's Loading frame replaces it until then), so the
  // request is taken exactly once, here: the upload step is preselected,
  // the rejected file pre-filled when the rejection allows it, the same
  // customer chosen. Read (peeked) in the initializer and dropped in the
  // effect below: StrictMode runs the initializer twice per mount and keeps
  // the SECOND result, so consuming here would lose the request in local
  // dev (skeptic round 2). Never read with the flag off = today's initial
  // state.
  const [retry] = useState<UploadRetryRequest | undefined>(() =>
    uploadPendingEnabled ? peekUploadRetryRequest() : undefined,
  );
  useEffect(() => {
    if (uploadPendingEnabled) clearUploadRetryRequest();
  }, []);
  const [state, setState] = useState(retry ? "upload" : "select");
  const [transferMethod, setTransferMethod] = useState<TransferMethod>();
  const [customer, setCustomer] = useState<Organization | null>(() => {
    const requested =
      retry?.customerId !== undefined
        ? customers?.find((org) => org.id === retry.customerId)
        : undefined;
    if (requested) return requested;
    return userRole === Role.EndUser && (customers?.length ?? 0) > 0
      ? customers![0]
      : null;
  });
  const [vendor, setVendor] = useState<Organization | null>(null);
  const [title, setTitle] = useState<string | undefined>();
  const [selectedFile, setSelectedFile] = useState<File | null>(
    retry?.file ?? null,
  );
  const [isDragging, setIsDragging] = useState(false);

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const isTitleValid = (title?.length ?? 0) <= 30;
  const isUploadValid = isTitleValid && selectedFile !== null;

  function handleDragCatalog(
    e: React.DragEvent<HTMLDivElement | HTMLFormElement>,
  ) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  }

  function handleDropCatalog(
    e: React.DragEvent<HTMLDivElement | HTMLFormElement>,
  ) {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.size > MAX_FILE_SIZE) {
        useErrorToast(t("orgScreens.fileSizeExceedsLimit"));
        return;
      }
      setSelectedFile(file);
    }
  }

  function handleSelectCatalog(event: ChangeEvent<HTMLInputElement>) {
    event.stopPropagation();
    event.preventDefault();

    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      if (file.size > MAX_FILE_SIZE) {
        useErrorToast(t("orgScreens.fileSizeExceedsLimit"));
        return;
      }
      setSelectedFile(file);
    }
  }

  const handleSubmit = (
    type: string,
    text?: string,
    color?: Color,
    font?: Font,
    size?: number,
    transferMethod?: TransferMethod,
  ) => {
    // For Admin users, use vendor ID if selected, otherwise use customer ID
    const organizationId =
      role === Role.Admin && vendor ? vendor.id : customer?.id;

    if (type === "upload") {
      if (selectedFile) {
        const finalTitle = title?.trim() || t("orgScreens.defaultLogoTitle");
        onUpload(selectedFile, finalTitle, organizationId);
      }
      return;
    }

    onCreate?.(type, text, color, font, size, transferMethod, organizationId);
  };

  const { options: logoOptions } = useLogoContext();
  const extensions = logoOptions.extensions;
  const inputRef = React.useRef<HTMLInputElement>(null);

  function onUploadClick() {
    inputRef.current?.click();
  }

  return (
    <div className={"flex flex-col space-y-4 p-4"}>
      <p className={"text-left text-base font-bold"}>
        {t("orgScreens.createNewLogo")}
      </p>
      <div className={"flex flex-col justify-center space-y-4"}>
        {state === "upload" && (
          <InputWrapper>
            <InputLabel>{t("title")}</InputLabel>
            <CustomInput
              value={title}
              onChange={(x) => setTitle(x.target.value)}
              maxLength={30}
              placeholder={t("orgScreens.enterLogoTitlePlaceholder")}
              className="!text-xs !bg-white"
            />
          </InputWrapper>
        )}
        {state !== "select" && role === Role.Admin && (
          <InputWrapper>
            <InputLabel className="mb-1">{t("vendor")}</InputLabel>
            <VendorSearchAutoComplete
              value={vendor}
              onChange={(selectedVendor) => setVendor(selectedVendor)}
              placeholder={t("orgScreens.searchForVendorPlaceholder")}
            />
          </InputWrapper>
        )}
        {state !== "select" && role !== Role.Admin && role !== Role.EndUser && (
          <InputWrapper>
            <InputLabel>{t("orgScreens.customerOptional")}</InputLabel>
            <CustomAutoComplete<Organization>
              options={customers}
              getOptionKey={(org) => org.id}
              displayOption={(org) => org.name}
              key={customer?.id}
              onChange={(org) => setCustomer(org)}
              value={customer}
              placeholder={t("orgScreens.searchForCustomerPlaceholder")}
            />
          </InputWrapper>
        )}
        {onCreate && (
          <SimpleLogoPanel
            state={state}
            setState={setState}
            setTransferMethod={setTransferMethod}
            transferMethod={transferMethod}
            options={options}
            onSubmit={handleSubmit}
            isUploadValid={isUploadValid}
            getTextPreview={(value, color, height, font) => {
              // For Admin users, use vendor ID if selected, otherwise use customer ID or orgId
              const previewOrgId =
                role === Role.Admin && vendor
                  ? vendor.id
                  : customer?.id || orgId;
              return generateTextLogoPreview(
                value,
                color.id,
                height,
                font.key,
                previewOrgId,
              );
            }}
            getNumberPreview={(color, height, font) => {
              // For Admin users, use vendor ID if selected, otherwise use customer ID or orgId
              const previewOrgId =
                role === Role.Admin && vendor
                  ? vendor.id
                  : customer?.id || orgId;
              return generateNumberLogoPreview(
                color.id,
                height,
                font.key,
                previewOrgId,
              );
            }}
            renderLogoUpload={() => (
              <div>
                <InputWrapper>
                  <InputLabel>{t("upload_logo")}</InputLabel>
                  <div
                    className={`w-full flex justify-center items-center border-1 transition-colors duration-200 py-12 rounded-md ${"bg-slate-100 hover:bg-slate-200 cursor-pointer border-slate-200 hover:border-slate-300"}`}
                    onClick={onUploadClick}
                    onDragEnter={handleDragCatalog}
                    onDragLeave={handleDragCatalog}
                    onDragOver={handleDragCatalog}
                    onDrop={handleDropCatalog}
                  >
                    <div className={"space-y-3 flex flex-col items-center"}>
                      <FontAwesomeIcon
                        icon={faFileArrowDown}
                        size={"2x"}
                        className={"mx-auto text-gray-600"}
                      />
                      <p className={"font-medium text-xs"}>
                        {selectedFile
                          ? selectedFile.name
                          : t("orgScreens.clickToChooseFileOrDropLogo")}
                      </p>
                      <div className="text-center text-[10px] text-gray-500 mt-2">
                        <p>{t("orgScreens.maxFileSize")}</p>
                      </div>
                    </div>
                  </div>
                  <input
                    ref={inputRef}
                    className={"hidden"}
                    type="file"
                    id="input-file-upload"
                    onChange={handleSelectCatalog}
                    accept={extensionsToMimeTypesString(extensions)}
                  />
                </InputWrapper>
              </div>
            )}
          />
        )}
      </div>
    </div>
  );
}
